import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAdvancedSearch, useSuggestedProducts, useSearchAnalytics } from '../hooks/useAdvancedSearch';
import { CityContext } from '../contexts/CityContext';
import { UserContext } from '../contexts/UserContext';
import ProductsGrid from '../components/ProductsGrid';
import ProductFilters from '../components/ProductFilters';
import { ProductGridSkeleton, SearchLoadingSkeleton } from '../components/Skeletons';
import SearchResultsSkeleton from '../components/SearchResultsSkeleton';
import { handleLikeClick as likeHandler } from '../utils/likeHandler';
import { filterPromotedProducts } from '../utils/promotionUtils';
import config from '../config';
import '../styles/dashboard.css';
import '../styles/searchResults.css';
import '../styles/searchResultsSkeleton.css';
import '../styles/productFilters.css';

// Default categories if API fails
const DEFAULT_CATEGORIES = [
  { id: 0, name: 'Tout' },
  { id: 1, name: 'Électronique', icon: 'fa-mobile-alt' },
  { id: 2, name: 'Mode', icon: 'fa-tshirt' },
  { id: 3, name: 'Maison', icon: 'fa-home' },
  { id: 4, name: 'Véhicules', icon: 'fa-car' },
  { id: 5, name: 'Services', icon: 'fa-concierge-bell' },
];

const SearchResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { redirectToLogin } = useContext(UserContext);

  // City context
  const { cities } = useContext(CityContext);

  // URL parameters
  const params = new URLSearchParams(location.search);
  const initialQuery = params.get('query') || '';
  const categoryParam = params.get('category') || '0';

  // State for categories
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [selectedCategory, setSelectedCategory] = useState(categoryParam);

  // Advanced search hook
  const {
    query,
    results: products,
    loading,
    error,
    hasMore,
    loadMore,
    refreshSearch,
    handleQueryChange,
    setQuery,
    setResults,
    initialized,
  } = useAdvancedSearch(initialQuery, { category: selectedCategory });

  // Suggested products hook
  const {
    suggestedProducts,
    loading: suggestionsLoading,
    loadingMore: suggestionsLoadingMore,
    hasMore: hasMoreSuggestions,
    loadMore: loadMoreSuggestions,
  } = useSuggestedProducts();

  // Search analytics
  const { trackSearchClick } = useSearchAnalytics();

  // Local state
  const [likedProducts, setLikedProducts] = useState([]);
  const [loggedInUser, setLoggedInUser] = useState(null);

  // Filter state
  const [filters, setFilters] = useState({
    sortBy: '',
    condition: '',
    minPrice: '',
    maxPrice: '',
    city: '',
    negotiable: false,
    promotedOnly: false
  });
  const [filteredProducts, setFilteredProducts] = useState(products);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${config.backendUrl}/products/categories/`);
        if (response.ok) {
          const data = await response.json();
          const categoriesData = data.results || data;

          if (Array.isArray(categoriesData) && categoriesData.length > 0) {
            const mapped = categoriesData.map(c => ({ id: c.id, name: c.name || c.category_name || '', icon: c.icon || c.image }));
            setCategories([{ id: 0, name: 'Tout', icon: 'fa-th-large' }, ...mapped]);
          }
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Handle category selection
  const handleCategoryChange = useCallback(
    (categoryId) => {
      setSelectedCategory(categoryId);
      const newParams = new URLSearchParams(location.search);
      newParams.set('category', categoryId);
      if (query && !newParams.has('query')) newParams.set('query', query);
      navigate(`/search?${newParams.toString()}`, { replace: true });
      if (query) refreshSearch();
    },
    [navigate, location.search, query, refreshSearch]
  );

  // Handle product click with analytics
  const handleProductClick = useCallback(
    (productUuid, event) => {  // Changed to accept event as second parameter
      // Add animation effect
      const card = event?.currentTarget;
      if (card) {
        card.classList.add('clicked');
        setTimeout(() => {
          card.classList.remove('clicked');
        }, 600);
      }
      
      const position = typeof event === 'number' ? event : 0; // Support legacy position parameter
      trackSearchClick(query, productUuid, position);
      navigate(`/products/view/${productUuid}`);
    },
    [navigate, query, trackSearchClick]
  );

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refreshSearch();
  }, [refreshSearch]);

  // Sync URL query param -> hook 
  useEffect(() => {
    const currentParams = new URLSearchParams(location.search);
    const newQuery = currentParams.get('query') || '';
    const newCategory = currentParams.get('category') || '0';

    // Update category if it changed
    if (newCategory !== selectedCategory) {
      setSelectedCategory(newCategory);
    }

    // Update query if it's different from current hook state
    if (newQuery !== query) {

      handleQueryChange(newQuery);
    } else if (newQuery && newQuery === query && newCategory !== selectedCategory) {
      // If query is the same but category changed, refresh search

      refreshSearch();
    }
  }, [location.search]); // Only depend on location.search to avoid infinite loops

  // Local wrapper for like clicks that mirrors Dashboard behavior and suppresses notifications
  const onLikeClick = useCallback(
    async (productId, isLikedByUser) => {
      await likeHandler({
        productId,
        isLikedByUser,
        loggedInUser,
        setLikedProducts,
        setProducts: setResults,
        navigate,
        // Suppress notification UI on search page
        setNotification: () => {},
        redirectToLogin,
      });
    },
    [loggedInUser, setResults, navigate]
  );

  // Filter products based on current filters
  const applyFilters = useCallback((productsToFilter, currentFilters) => {
    let filtered = [...productsToFilter];

    // Condition filter
    if (currentFilters.condition) {
      filtered = filtered.filter(product => 
        product.condition && product.condition.toLowerCase() === currentFilters.condition.toLowerCase()
      );
    }

    // Price range filter
    if (currentFilters.minPrice || currentFilters.maxPrice) {
      filtered = filtered.filter(product => {
        const price = parseFloat(product.price) || 0;
        const min = parseFloat(currentFilters.minPrice) || 0;
        const max = parseFloat(currentFilters.maxPrice) || Infinity;
        return price >= min && (max === Infinity || price <= max);
      });
    }

    // City filter
    if (currentFilters.city) {
      filtered = filtered.filter(product => 
        product.city_name && product.city_name.toLowerCase() === currentFilters.city.toLowerCase()
      );
    }

    // Negotiable filter
    if (currentFilters.negotiable) {
      filtered = filtered.filter(product => product.negotiable === true);
    }

    // Promoted only filter
    if (currentFilters.promotedOnly) {
      filtered = filterPromotedProducts(filtered);
    }

    // Sort products
    if (currentFilters.sortBy) {
      switch (currentFilters.sortBy) {
        case 'price_low':
          filtered.sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0));
          break;
        case 'price_high':
          filtered.sort((a, b) => (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0));
          break;
        case 'newest':
          filtered.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
          break;
        case 'oldest':
          filtered.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
          break;
        case 'most_liked':
          filtered.sort((a, b) => (b.likes || 0) - (a.likes || 0));
          break;
        case 'most_viewed':
          filtered.sort((a, b) => (b.views || 0) - (a.views || 0));
          break;
        default:
          break;
      }
    }

    return filtered;
  }, []);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    const filtered = applyFilters(products, newFilters);
    setFilteredProducts(filtered);
  }, [products, applyFilters]);

  // Apply filters when products change
  useEffect(() => {
    const filtered = applyFilters(products, filters);
    setFilteredProducts(filtered);
  }, [products, filters, applyFilters]);

  // Debug logging (kept minimal)
  useEffect(() => {
    // State tracking (no console output)
  }, [query, selectedCategory, products, loading, error, initialized]);

  // Show loading skeleton for completely initial state (no query, not initialized)
  if (!initialized && !query && loading) {
    return <SearchResultsSkeleton />;
  }

  // Show searching skeleton when actively searching (regardless of previous results)
  if (loading && query && query.trim().length >= 2) {
    return <SearchLoadingSkeleton showCategories={categories.length > 0} showSuggestions={false} />;
  }

  return (
    <div className="search-results-page">
      <div className="search-header-container">
        <div className="search-header">
          <h1 className="search-title">{query ? `Résultats pour "${query}"` : 'Recherche'}</h1>
          {categories.length > 0 && (
            <div className="categories-container">
              {categories.map((category) => (
                <button
                  key={category.id}
                  className={`category-pill ${String(selectedCategory) === String(category.id) ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(category.id)}
                >
                  {category.icon && <i className={`fas ${category.icon} category-icon`}></i>}
                  {category.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="search-results-error-container">
          <div className="search-results-error-message">
            <i className="fas fa-exclamation-triangle" style={{ color: '#FFD600' }}></i>
            <span>{error || 'Une erreur est survenue. Veuillez réessayer.'}</span>
            <button className="retry-button" onClick={handleRefresh}>
              Réessayer
            </button>
          </div>
        </div>
      )}

      {loading && query ? (
        <SearchResultsSkeleton />
      ) : (
        <div className="search-results-content">
          {!query ? (
            <div className="search-welcome">
              <div className="welcome-icon">
                <i className="fas fa-search"></i>
              </div>
              <h2>Découvrez des milliers de produits</h2>
              <p>Commencez par taper votre recherche dans la barre de navigation</p>
            </div>
          ) : products.length > 0 ? (
            <>
              {/* Results Count Banner */}
              <div className="results-count-banner">
                <div className="results-count-info">
                  <i className="fas fa-search-plus"></i>
                  <span className="results-count-text">
                    {filteredProducts.length} résultat{filteredProducts.length !== 1 ? 's' : ''} trouvé{filteredProducts.length !== 1 ? 's' : ''}
                  </span>
                  {filteredProducts.length !== products.length && (
                    <span className="results-total-text">sur {products.length} au total</span>
                  )}
                </div>
                {Object.values(filters).some(v => v && v !== '') && (
                  <div className="active-filters-badge">
                    <i className="fas fa-filter"></i>
                    <span>Filtres actifs</span>
                  </div>
                )}
              </div>

              {/* Enhanced Filters Section */}
              <div className="enhanced-filters-section">
                <ProductFilters 
                  filters={filters}
                  onFiltersChange={handleFilterChange}
                  productsCount={filteredProducts.length}
                  totalProducts={products.length}
                />
              </div>

              <ProductsGrid
                products={filteredProducts}
                likedProducts={likedProducts}
                loggedInUser={loggedInUser}
                handleProductClick={handleProductClick}
                handleLikeClick={onLikeClick}
                hasMore={hasMore}
                fetchMoreData={loadMore}
              />
            </>
          ) : !loading ? (
            <div className="no-results">
              <div className="no-results-icon">
                <i className="fas fa-exclamation-circle"></i>
              </div>
              <h3>Aucun résultat trouvé</h3>
              <p>Aucun produit ne correspond à "{query}"</p>
            </div>
          ) : null}

          {suggestedProducts.length > 0 && (
            <>
              <h2 className="section-title">Suggestions</h2>
              {suggestionsLoading && suggestedProducts.length === 0 ? (
                <SearchResultsSkeleton />
              ) : (
                <ProductsGrid
                  products={suggestedProducts}
                  likedProducts={likedProducts}
                  loggedInUser={loggedInUser}
                  handleProductClick={handleProductClick}
                  handleLikeClick={onLikeClick}
                  hasMore={hasMoreSuggestions}
                  fetchMoreData={loadMoreSuggestions}
                  loadingMore={suggestionsLoadingMore}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchResults;